// src/components/QuestionView.jsx
import React, { useEffect, useMemo, useState } from "react";
import styles from './QuestionView.module.scss';

//constants
import { getCashImage } from "constants/cash";
import { MESSAGES } from 'constants/constants'
const STEP = 25_000; // unit size for placing money

function roundToStep(v) {
    return Math.max(0, Math.floor(v / STEP) * STEP);
}

function QuestionView({ answers = [], playerMdmdData, room }) {
    const { totalMoney = 0, placedMoney = [] } = playerMdmdData ?? {};
    const [sliderValues, setSliderValues] = useState([...placedMoney]);
    const [lockCooldown, setLockCooldown] = useState(false);
    const [draggingIdx, setDraggingIdx] = useState(null);
    const [shakeError, setShakeError] = useState(false);

    //useEffect(() => {
    //    setSliderValues([...placedMoney]);
    //}, [placedMoney]);

    useEffect(() => {
        if (draggingIdx !== null) {
            window.addEventListener("mousemove", handleBoxPointerMove);
            window.addEventListener("mouseup", handleBoxPointerUp);
            window.addEventListener("touchmove", handleBoxPointerMove);
            window.addEventListener("touchend", handleBoxPointerUp);
            return () => {
                window.removeEventListener("mousemove", handleBoxPointerMove);
                window.removeEventListener("mouseup", handleBoxPointerUp);
                window.removeEventListener("touchmove", handleBoxPointerMove);
                window.removeEventListener("touchend", handleBoxPointerUp);
            };
        }
    }, [draggingIdx]);

    const horizontalPadding = 14; // match your SCSS

    const handleBoxPointer = (i, e) => {
        if (e.type === "mousedown" && e.button !== 0) return;
        e.preventDefault();
        const box = e.currentTarget;
        const rect = box.getBoundingClientRect();
        const rawValue = getSliderValueFromPointer(e, rect, totalMoney, horizontalPadding);
        handleSliderChange(i, rawValue);
        setDraggingIdx(i);
    };

    const handleBoxPointerMove = (e) => {
        if (draggingIdx === null) return;
        const box = document.getElementById(`answerBox-${draggingIdx}`);
        if (!box) return;
        const rect = box.getBoundingClientRect();
        const rawValue = getSliderValueFromPointer(e, rect, totalMoney, horizontalPadding);
        handleSliderChange(draggingIdx, rawValue);
    };

    function getSliderValueFromPointer(e, rect, totalMoney, horizontalPadding) {
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const x = clientX - rect.left - horizontalPadding;
        const trackWidth = rect.width - horizontalPadding * 2;
        const percent = Math.max(0, Math.min(1, x / trackWidth));
        return percent * totalMoney;
    }

    const handleBoxPointerUp = () => {
        if (draggingIdx !== null) {
            setSliderValues(current => {
                room?.send(MESSAGES.TO_SERVER.PLACE_MONEY, { placedMoney: current });
                return current;
            });
            setDraggingIdx(null);
        }
    };

    // "Draft" values prefer local slider state, fall back to server confirmation.
    const draft = sliderValues?.length ? sliderValues : [...placedMoney];

    const draftSum = useMemo(
        () => draft.reduce((a, b) => a + (b || 0), 0),
        [draft]
    );
    const bankProjected = Math.max(0, totalMoney - draftSum);

    const isMaxBoxesPlacedDraft = useMemo(() => {
        const nonZero = draft.filter((v) => (v || 0) > 0).length;
        return nonZero >= answers.length - 1;
    }, [answers, draft]);

    const handleSliderChange = (i, rawValue) => {
        // Sum of all other sliders (besides the one we're dragging)
        const sumOthers = draft.reduce(
            (sum, v, idx) => (idx === i ? sum : sum + (v || 0)),
            0
        );

        // Hard cap so total of draft never exceeds totalMoney
        const allowedMaxForThis = Math.max(0, totalMoney - sumOthers);

        let nextVal = Math.min(Math.max(0, rawValue), allowedMaxForThis);
        nextVal = roundToStep(nextVal);

        if (nextVal !== (draft[i] || 0)) {
            const next = [...draft];
            next[i] = nextVal;
            setSliderValues(next);
        }
    };

    const handleLockIn = () => {
        if (!lockCooldown) {
            // Check if trying to lock in without all money placed
            if (!playerMdmdData?.lockedIn && !allMoneyPlaced) {
                setShakeError(true);
                setTimeout(() => setShakeError(false), 500);
                return;
            }

            room?.send(MESSAGES.TO_SERVER.LOCK_IN, !playerMdmdData.lockedIn);
            setLockCooldown(true);
            setTimeout(() => setLockCooldown(false), 2000); // 2 seconds
        }
    };

    const formatToGBP = (n) => "£" + (n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 });

    const allMoneyPlaced = bankProjected === 0;
    const buttonDisabled = lockCooldown;
    const shouldGreyOut = !playerMdmdData?.lockedIn && !allMoneyPlaced;
    const cashColor = playerMdmdData?.cashColor;

    return (
        <div className={styles.questionView} onDragStart={(e) => e.preventDefault()}  onContextMenu={(e) => e.preventDefault()}>
            {/* Projected remaining (based on draft sliders) */}
            <div className={styles.bankTotal}>
                {formatToGBP(bankProjected)}
                <img src={getCashImage(cashColor, 4, 4)} alt="" aria-hidden className={styles.cashImg} />
            </div>

            <div className={styles.answerSection}>
                {answers.filter(Boolean).map((answerText, i) => {
                    const placedServer = placedMoney[i] || 0;
                    const sliderVal = draft[i] ?? placedServer;
                    const sliderPercent = totalMoney ? (sliderVal / totalMoney) : 0;
                    const disable = playerMdmdData?.lockedIn || (sliderVal === 0 && (isMaxBoxesPlacedDraft || allMoneyPlaced));

                    return (
                        <div id={`answerBox-${i}`} className={`${styles.answerBox} ${disable && styles.disabled}`}
                            key={i} onMouseDown={e => handleBoxPointer(i, e)} onTouchStart={e => handleBoxPointer(i, e)}>

                            <div className={styles.answerText}>{answerText}</div>

                            <div className={styles.sliderRow}>
                                <input type="range"
                                    min={0}
                                    max={totalMoney}
                                    step={STEP}
                                    value={sliderVal}
                                    disabled={disable}
                                    onChange={(e) => handleSliderChange(i, Number(e.target.value))}
                                    className={styles.slider}
                                    style={{ '--percent': sliderPercent }}
                                    aria-label={`Place money on answer ${i + 1}`}
                                />
                            </div>

                            <div className={styles.valuesRow}>
                                <div className={styles.min}></div>
                                <div className={styles.max}>{formatToGBP(sliderVal)}</div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className={styles.lockIn}>
                <p className={`${styles.instructionText} ${shakeError ? styles.shakeText : ''}`}>
                    Place all your cash, then lock in.
                </p>
                <button className={`${styles.button} ${shouldGreyOut ? styles.greyedOut : ''}`}
                    onClick={handleLockIn} disabled={buttonDisabled} >
                    {playerMdmdData?.lockedIn ? "CANCEL" : "LOCK IN"}
                </button>
            </div>
        </div>
    );
}

export default React.memo(QuestionView);